import {
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
} from '@angular/core';
import {
  ApplicationStore,
  getModuleGroupByName,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import { provideModuleWidgets, provideSoftAppsModule, withLazySoftAppsModuleTranslation, withSoftAppsModuleRoutes } from '@softapps/core';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import { default as de } from '../i18n/de.json';
import { default as deTitles } from '../i18n/de.titles.json';
import { SOFTLINE_API_JOPOSTEN, SOFTLINE_API_OFFENE_POSTEN } from './offene-posten.api';
import {
  SOFTLINE_FEATURE_OFFENE_POSTEN,
  SOFTLINE_FEATURE_OFFENE_POSTEN_JOPOSTEN,
  SOFTLINE_FEATURE_OFFENE_POSTEN_JOPOSTEN_DEBITOREN,
  SOFTLINE_FEATURE_OFFENE_POSTEN_JOPOSTEN_KREDITOREN,
  SOFTLINE_PERMISSIONS_OFFENE_POSTEN,
} from './offene-posten.shared';
import { JopostenStore, OffenePostenStore } from './store';
import { offenePostenRoutes } from './offene-posten.routes';
import { offenePostenWidgets } from './offene-posten.widgets';
import { provideOffenePostenGlobals } from './offene-posten.globals';

export function provideOffenePostenModule() {
  return [
    provideSoftAppsModule(
      {
        name: 'offenePosten',
        title: '#OFFENE_POSTEN.TITLE',
        icon: 'fa-regular fa-money-check',
        route: '/offene-posten',
        permission: SOFTLINE_PERMISSIONS_OFFENE_POSTEN,
        group: getModuleGroupByName('Finanzbuchhaltung'),
      },
      withSoftAppsModuleRoutes({ app: offenePostenRoutes }),
      withLazySoftAppsModuleTranslation({
        eager: { de: deTitles },
        lazy: { de: () => import('../i18n/de.json') },
      })
    ),
    ...provideOffenePostenGlobals(),
    // legacy path only — the federation shell registers these via ./ModuleWidgets
    ...provideModuleWidgets(offenePostenWidgets()),
  ];
}

export const offenePostenProviders = [
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_OFFENE_POSTEN,
      feature: OffenePostenStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_OFFENE_POSTEN),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_OFFENE_POSTEN_JOPOSTEN,
      feature: JopostenStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_JOPOSTEN),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_OFFENE_POSTEN_JOPOSTEN_DEBITOREN,
      feature: JopostenStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_JOPOSTEN),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_OFFENE_POSTEN_JOPOSTEN_KREDITOREN,
      feature: JopostenStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_JOPOSTEN),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    })
  ),
  provideEnvironmentInitializer(() => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'offenePosten',
      language: 'de',
      translations: de,
    });
    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: '#OFFENE_POSTEN.TITLE',
      icon: 'fa-regular fa-money-check',
      route: '/offene-posten',
      permission: SOFTLINE_PERMISSIONS_OFFENE_POSTEN,
      group: getModuleGroupByName('Finanzbuchhaltung'),
    });
  }),
  ...provideModuleWidgets(offenePostenWidgets()),
];
